import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { DEMO_MESSAGES, DEMO_MATCHES } from '../data/demo';
import Avatar from '../components/ui/Avatar';
import Button from '../components/ui/Button';
import { FiArrowLeft, FiSend, FiImage, FiMic, FiSmile, FiVideo, FiPhone } from 'react-icons/fi';

const Chat = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState(DEMO_MESSAGES[matchId] || DEMO_MESSAGES['m1'] || []);
  const [text, setText] = useState('');
  const [showAI, setShowAI] = useState(false);
  const [typing, setTyping] = useState(false);
  const messagesEnd = useRef();
  const match = DEMO_MATCHES.find(m => m._id === matchId) || DEMO_MATCHES[0];

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTyping(true);
      setTimeout(() => setTyping(false), 2000);
    }, 5000);
    return () => clearTimeout(timer);
  }, [messages.length]);

  const send = () => {
    if (!text.trim()) return;
    setMessages(p => [...p, { _id: Date.now().toString(), sender: 'me', text: text.trim(), time: 'Now', read: false }]);
    setText('');
  };

  const aiSuggestions = [
    'Hey! I noticed we both love travel. What is your dream destination?',
    'Your profile is amazing! What got you into your profession?',
    'I see you love coffee. Do you have a favorite cafe?',
    'What is the best trip you have ever taken?',
  ];

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#08080C' }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
        borderBottom: '1px solid rgba(255,255,255,0.04)', background: 'rgba(17,17,24,0.8)',
        backdropFilter: 'blur(20px)',
      }}>
        <button onClick={() => navigate('/messages')} style={{
          background: 'none', border: 'none', color: '#A1A1AA', cursor: 'pointer', padding: 4,
        }}><FiArrowLeft size={22} /></button>
        <Avatar src={match.user.photos?.[0]} name={match.user.name} size={40} online={match._id === 'm1'} />
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600 }}>{match.user.name}</h3>
          {typing ? (
            <p style={{ fontSize: 12, color: '#FF3D71', fontWeight: 500 }}>typing...</p>
          ) : (
            <p style={{ fontSize: 12, color: '#52525B' }}>{match._id === 'm1' ? 'Online' : 'Last seen 2h ago'}</p>
          )}
        </div>
        <button style={{ background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 12, width: 36, height: 36, color: '#A1A1AA', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FiPhone size={16} />
        </button>
        <button style={{ background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 12, width: 36, height: 36, color: '#A1A1AA', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FiVideo size={16} />
        </button>
      </div>

      {/* AI Assistant bar */}
      <button onClick={() => setShowAI(!showAI)} style={{
        margin: '8px 16px', padding: '10px 14px', borderRadius: 14,
        background: showAI ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${showAI ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.04)'}`,
        color: showAI ? '#A855F7' : '#71717A',
        fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'Inter',
        display: 'flex', alignItems: 'center', gap: 8, width: 'calc(100% - 32px)',
      }}>✨ AI Assistant — Get conversation ideas</button>

      {showAI && (
        <div style={{ padding: '0 16px 8px', display: 'flex', gap: 8, overflowX: 'auto' }}>
          {aiSuggestions.map((s, i) => (
            <button key={i} onClick={() => { setText(s); setShowAI(false); }} style={{
              flexShrink: 0, padding: '10px 14px', borderRadius: 14,
              background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)',
              color: '#D4D4D8', fontSize: 12, maxWidth: 200, textAlign: 'left',
              cursor: 'pointer', fontFamily: 'Inter', lineHeight: 1.4,
            }}>{s}</button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <p style={{ fontSize: 12, color: '#52525B' }}>You matched on Sep 7</p>
        </div>
        {messages.map((msg, i) => {
          const isMe = msg.sender === 'me';
          return (
            <div key={msg._id} style={{
              display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start',
              marginBottom: 6, animation: 'slideUp 0.2s ease',
            }}>
              <div style={{
                maxWidth: '78%', padding: '10px 14px', borderRadius: 18,
                borderBottomRightRadius: isMe ? 4 : 18,
                borderBottomLeftRadius: isMe ? 18 : 4,
                background: isMe
                  ? 'linear-gradient(135deg, #FF3D71, #8B5CF6)'
                  : 'rgba(255,255,255,0.06)',
                color: '#FFFFFF', fontSize: 14, lineHeight: 1.45,
              }}>
                <p>{msg.text}</p>
                <p style={{ fontSize: 10, color: isMe ? 'rgba(255,255,255,0.6)' : '#52525B', marginTop: 4, textAlign: 'right' }}>
                  {msg.time} {isMe ? (msg.read ? '✓✓' : '✓') : ''}
                </p>
              </div>
            </div>
          );
        })}
        {typing && (
          <div style={{ display: 'flex', gap: 4, padding: '12px 16px', width: 60 }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 8, height: 8, borderRadius: 4, background: '#71717A',
                animation: `breathe 1.2s ease-in-out ${i * 0.2}s infinite`,
              }} />
            ))}
          </div>
        )}
        <div ref={messagesEnd} />
      </div>

      {/* Input */}
      <div style={{
        padding: '10px 12px', display: 'flex', gap: 8, alignItems: 'center',
        borderTop: '1px solid rgba(255,255,255,0.04)', background: 'rgba(17,17,24,0.8)',
        paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
      }}>
        <button style={{ background: 'none', border: 'none', color: '#71717A', cursor: 'pointer', padding: 6 }}>
          <FiImage size={20} />
        </button>
        <button style={{ background: 'none', border: 'none', color: '#71717A', cursor: 'pointer', padding: 6 }}>
          <FiMic size={20} />
        </button>
        <input value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          placeholder="Type a message..."
          style={{
            flex: 1, padding: '10px 14px', background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.06)', borderRadius: 24,
            color: '#FFFFFF', fontSize: 14, fontFamily: 'Inter', outline: 'none',
          }} />
        <button onClick={send} style={{
          width: 40, height: 40, borderRadius: 20, border: 'none',
          background: text.trim() ? 'linear-gradient(135deg, #FF3D71, #8B5CF6)' : 'rgba(255,255,255,0.06)',
          color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s',
        }}><FiSend size={16} /></button>
      </div>
    </div>
  );
};

export default Chat;
